// /src/engine/ldrawColors.ts

// Minimal LDraw-ish palette used by the prototype.
// If you install the full LDraw library, you'll still get correct geometry;
// this just drives UI and fallback materials.

import { LDRAW_BASE } from './ldrawConfig';

export type LDrawColor = { id: number; name: string; hex: string; edgeHex?: string; alpha?: number; material?: string };

export const COLORS: LDrawColor[] = [
  { id: 0, name: 'Black', hex: '#101010' },
  { id: 1, name: 'White', hex: '#ffffff' },
  { id: 2, name: 'Blue', hex: '#0055bf' },
  { id: 4, name: 'Red', hex: '#c91a09' },
  { id: 10, name: 'Dark Gray', hex: '#6d6e5c' },
  { id: 14, name: 'Yellow', hex: '#f2cd37' },
  { id: 71, name: 'Light Bluish Gray', hex: '#a0a5a9' },
  { id: 72, name: 'Dark Bluish Gray', hex: '#646464' },
  { id: 16, name: 'Main Color', hex: '#c0c0c0' }
];

let LOADED: LDrawColor[] | null = null;
let LOAD_PROMISE: Promise<LDrawColor[]> | null = null;

function parseLDConfig(text: string): LDrawColor[] {
  const out: LDrawColor[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!/^0\s+!COLOUR\b/i.test(line)) continue;

    const m = line.match(/^0\s+!COLOUR\s+(.+?)\s+CODE\s+(\d+)\s+VALUE\s+(#[0-9a-fA-F]{6})\b/i);
    if (!m) continue;
    const name = m[1].trim();
    const id = Number.parseInt(m[2], 10);
    const hex = m[3].toLowerCase();
    if (!Number.isFinite(id)) continue;

    const edge = line.match(/\bEDGE\s+(#[0-9a-fA-F]{6})\b/i)?.[1]?.toLowerCase();
    const alpha = line.match(/\bALPHA\s+(\d+)\b/i)?.[1];
    const material = line.match(/\bMATERIAL\s+(\S+)\b/i)?.[1];

    out.push({
      id,
      name,
      hex,
      edgeHex: edge,
      alpha: alpha ? Number(alpha) : undefined,
      material: material ? String(material) : undefined,
    });
  }

  out.sort((a, b) => a.id - b.id);
  const seen = new Set<number>();
  const uniq: LDrawColor[] = [];
  for (const c of out) {
    if (seen.has(c.id)) continue;
    seen.add(c.id);
    uniq.push(c);
  }
  if (!uniq.some((c) => c.id === 16)) uniq.push({ id: 16, name: 'Main Color', hex: '#c0c0c0' });
  return uniq.length ? uniq : COLORS;
}

export async function loadLDrawColors(basePath = LDRAW_BASE): Promise<LDrawColor[]> {
  if (LOADED) return LOADED;
  if (LOAD_PROMISE) return LOAD_PROMISE;
  const base = basePath.endsWith('/') ? basePath : basePath + '/';
  LOAD_PROMISE = (async () => {
    try {
      const res = await fetch(`${base}LDConfig.ldr`, { cache: 'force-cache' as any });
      if (!res.ok) {
        LOADED = COLORS;
        return LOADED;
      }
      const text = await res.text();
      LOADED = parseLDConfig(text);
      return LOADED;
    } catch {
      LOADED = COLORS;
      return LOADED;
    }
  })();
  return LOAD_PROMISE;
}

export function colorToHex(id: number): string {
  const list = LOADED ?? COLORS;
  return list.find((c) => c.id === id)?.hex ?? '#c0c0c0';
}
